// src/app/timesheets/components/weekly-timesheet.component.ts
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { EmployeeTimesheetService } from '../../services/employee-timesheet.service';
import {
  ApiCompanyListItem,
  ApiDepartmentListItem,
  DailyBookedHours,
  DailyLog,
  User,
  WeeklyBookedHours,
  WeeklyReportData,
  WeeklyReportFilters,
  WeeklyTimesheet,
  WeeklyTimesheetResponse,
  YywwListItem,
} from '../../models/timesheet.model';

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

import { Observable, Subject, forkJoin, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  take,
  tap,
} from 'rxjs/operators';

import { DateUtils, WeekInfo } from '../../utils/date-utils';
import { FilterService } from '../../../services/filter.service';

dayjs.extend(isoWeek);

type ToastKind = 'success' | 'error' | 'info';
type TotalClass = 'total-scheduled-met' | 'total-scheduled-under' | 'total-scheduled-none';

interface WeeklyTimesheetWithMeta extends WeeklyTimesheet {
  formattedTimeLogs: string[];
  bookedTooltips: string[];
  clockedHours: number[];
  bookedHours: number[];
  displayHours: {
    number: number;
    backgroundColor: string;
  }[];
  dailyClasses: string[];
  totalClass: TotalClass;
  bookedWeeklyHours: number;
  scheduledWeeklyHours: number;
  bookedVsScheduledLabel: string;

  remainingHoursToForty: number;
  autoFillToFortyChecked: boolean;
}

interface BookedDayMeta {
  hours: number;
  tooltip: string;
  hasHoliday: boolean;
  hasRequestedOfftime: boolean;
  isFullySupervisorApproved: boolean;
}

interface WeeklyScheduleMeta {
  scheduledHours: number;
  dailyScheduledHours: number[];
}

interface WeeklyTimesheetFiltersVm {
  companyIds: number[] | null;
  departmentIds: number[] | null;
  employeeId: number | null;
  searchTerm: string;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

@Component({
  selector: 'app-weekly-timesheet',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './weekly-timesheet.component.html',
  styleUrls: ['./weekly-timesheet.component.scss'],
})
export class WeeklyTimesheetComponent implements OnInit, OnDestroy {
  filterService = inject(FilterService);
  timesheets$!: Observable<WeeklyTimesheetWithMeta[]>;

  currentWeek: WeekInfo = DateUtils.getCurrentWeek();
  isLoading = true;
  errorMessage: string | null = null;

  dailyTotals: number[] = Array(7).fill(0);
  dailyAverages: number[] = Array(7).fill(0);
  weeklyTotal = 0;
  overallAverage = 0;

  weeksIsLoading = false;
  weeksError: string | null = null;
  weeks: YywwListItem[] = [];

  filters: WeeklyTimesheetFiltersVm = {
    companyIds: [],
    departmentIds: [],
    employeeId: null,
    searchTerm: '',
  };

  exportStartWeek: string | null = null;
  exportEndWeek: string | null = null;
  private userTouchedExportRange = false;

  currentRows: WeeklyTimesheetWithMeta[] = [];
  isSavingAutoFill = false;
  autoFillError: string | null = null;

  toastMessage: string | null = null;
  toastKind: ToastKind = 'info';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private isDestroyed = false;

  private readonly searchInput$ = new Subject<string>();
  private readonly employeeIdInput$ = new Subject<number | null>();

  constructor(
    private readonly timesheetService: EmployeeTimesheetService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.bindSearch();
    this.bindEmployeeId();

    const urlWeek = this.route.snapshot.queryParamMap.get('week');

    if (urlWeek && this.isValidFullYearWeek(urlWeek)) {
      this.currentWeek = this.buildWeekInfoFromFullYearWeek(urlWeek);
    } else {
      this.syncWeekToUrl(true);
    }

    this.loadWeeksForYear(this.currentWeek.startDate.year());
    this.loadTimesheets();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;

    this.searchInput$.complete();
    this.employeeIdInput$.complete();

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }

  private bindSearch(): void {
    this.searchInput$.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((value) => {
      this.filters.searchTerm = value;
      this.loadTimesheets();
    });
  }

  private bindEmployeeId(): void {
    this.employeeIdInput$.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((value) => {
      this.filters.employeeId = value;
      this.loadTimesheets();
    });
  }

  private safeDetectChanges(): void {
    if (!this.isDestroyed) {
      this.cdr.detectChanges();
    }
  }

  private showToast(message: string, kind: ToastKind = 'info', ms = 3500): void {
    this.toastMessage = message;
    this.toastKind = kind;
    this.safeDetectChanges();

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = setTimeout(() => {
      this.toastMessage = null;
      this.toastTimer = null;
      this.safeDetectChanges();
    }, ms);
  }

  clearToast(): void {
    this.toastMessage = null;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    this.safeDetectChanges();
  }

  openAdminTimesheet(userId: number): void {
    if (!userId) return;

    const week = this.toFullYearWeek6(this.currentWeek.yyyyww);

    this.router.navigate(['/timesheets/admin', userId, week], {
      queryParams: {
        returnTo: 'weekly',
        returnWeek: week,
        returnUrl: this.router.url,
      },
    });
  }

  private formatLocalTime(value?: string | null): string {
    if (!value) return 'N/A';

    const date = new Date(`${value}Z`);

    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private getWeekDateKeys(): string[] {
    return Array.from({ length: 7 }, (_, index) =>
      this.currentWeek.startDate.clone().add(index, 'day').format('YYYY-MM-DD'),
    );
  }

  private toDateKey(value?: string | null): string {
    if (!value) return '';

    return String(value).slice(0, 10);
  }

  private buildLogsByDate(logs: DailyLog[] | undefined | null): Map<string, DailyLog> {
    const byDate = new Map<string, DailyLog>();

    for (const day of logs ?? []) {
      const dateKey = this.toDateKey(day?.date);

      if (!dateKey) {
        continue;
      }

      const existing = byDate.get(dateKey);

      byDate.set(dateKey, {
        ...day,
        date: dateKey,
        timeLogs: [...(existing?.timeLogs ?? []), ...(day.timeLogs ?? [])],
      });
    }

    return byDate;
  }

  private round2(value: number): number {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  private buildWeeklyFilters(fullYearWeek?: string): WeeklyReportFilters {
    return {
      fullYearWeek: fullYearWeek ?? this.toFullYearWeek6(this.currentWeek.yyyyww),
      companyIds: this.filters.companyIds?.length ? this.filters.companyIds : undefined,
      departmentIds: this.filters.departmentIds?.length ? this.filters.departmentIds : undefined,
      employeeId: this.filters.employeeId,
      searchTerm: this.filters.searchTerm.trim() || null,
    };
  }

  prevWeek(): void {
    this.currentWeek = DateUtils.getPreviousWeek(this.currentWeek);
    this.syncWeekToUrl();
    this.loadTimesheets();
    this.loadWeeksForYear(this.currentWeek.startDate.year());
    this.setDefaultExportRangeFromWeeks();
  }

  nextWeek(): void {
    this.currentWeek = DateUtils.getNextWeek(this.currentWeek);
    this.syncWeekToUrl();
    this.loadTimesheets();
    this.loadWeeksForYear(this.currentWeek.startDate.year());
    this.setDefaultExportRangeFromWeeks();
  }

  getDayLabel(dayIndex: number): string {
    return DAY_LABELS[dayIndex] ?? '';
  }

  get hasAnyAutoFillSelected(): boolean {
    return false;
  }

  get selectedAutoFillCount(): number {
    return 0;
  }

  get eligibleAutoFillCount(): number {
    return 0;
  }

  get hasActiveFilters(): boolean {
    return (
      !!this.filters.companyIds?.length ||
      !!this.filters.departmentIds?.length ||
      this.filters.employeeId !== null ||
      !!this.filters.searchTerm.trim()
    );
  }

  onCompanyChange(value: number | string | null): void {
    const numValue = this.toNullableNumber(value);
    this.filters.companyIds = numValue !== null ? [numValue] : [];
    this.loadTimesheets();
  }

  onDepartmentChange(value: number | string | null): void {
    const numValue = this.toNullableNumber(value);
    this.filters.departmentIds = numValue !== null ? [numValue] : [];
    this.loadTimesheets();
  }

  onEmployeeIdChange(value: number | string | null): void {
    const normalized = this.toNullableNumber(value);
    this.employeeIdInput$.next(normalized);
  }

  onSearchTermChange(value: string | null): void {
    const normalized = String(value ?? '').trimStart();
    this.searchInput$.next(normalized);
  }

  clearFilters(): void {
    this.filters = {
      companyIds: [],
      departmentIds: [],
      employeeId: null,
      searchTerm: '',
    };

    this.loadTimesheets();
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  toggleAutoFill(_: WeeklyTimesheetWithMeta): void {}

  clearAutoFillSelections(): void {}

  selectAllEligibleAutoFill(): void {}

  saveAutoFillToForty(): void {
    this.showToast('Fill-to-40 booking has been removed. The total column is now visual only.', 'info');
  }

  loadTimesheets(showOverlay = true): void {
    if (showOverlay) {
      this.isLoading = true;
      this.safeDetectChanges();
    }

    this.errorMessage = null;
    this.autoFillError = null;

    const weekKeys = this.getWeekDateKeys();
    const filters = this.buildWeeklyFilters();

    this.timesheets$ = this.timesheetService.getWeeklyTimesheetsByFilters(filters).pipe(
      map((response: WeeklyTimesheetResponse) => {
        const rows = this.mapWeeklyRows(response?.data, weekKeys, this.currentRows);

        this.currentRows = rows;

        return rows;
      }),
      tap((rows: WeeklyTimesheetWithMeta[]) => this.computeSummaryRows(rows)),
      catchError(() => {
        this.errorMessage = 'Failed to load timesheets';
        this.currentRows = [];
        this.computeSummaryRows([]);
        this.showToast('Failed to load timesheets.', 'error');

        return of([] as WeeklyTimesheetWithMeta[]);
      }),
      finalize(() => {
        if (showOverlay) {
          this.isLoading = false;
        }

        this.safeDetectChanges();
      }),
    );
  }

  private computeSummaryRows(rows: WeeklyTimesheetWithMeta[]): void {
    const employeeCount = rows.length || 1;

    this.dailyTotals = Array(7).fill(0);

    for (const row of rows) {
      row.displayHours.forEach((hours, index) => {
        this.dailyTotals[index] += Number(hours?.number) || 0;
      });
    }

    this.weeklyTotal = this.round2(this.dailyTotals.reduce((sum, value) => sum + value, 0));
    this.dailyAverages = this.dailyTotals.map((total) => this.round2(total / employeeCount));
    this.overallAverage = this.round2(this.weeklyTotal / (employeeCount * 7));
  }

  onExportStartChange(value: string | null): void {
    this.userTouchedExportRange = true;
    this.exportStartWeek = value;
  }

  onExportEndChange(value: string | null): void {
    this.userTouchedExportRange = true;
    this.exportEndWeek = value;
  }

  private loadWeeksForYear(year: number): void {
    this.weeksIsLoading = true;
    this.weeksError = null;
    this.safeDetectChanges();

    forkJoin([
      this.timesheetService.getYywwsAll(String(year), 200),
      this.timesheetService.getYywwsAll(String(year - 1), 200),
    ])
      .pipe(
        take(1),
        catchError(() => {
          this.weeksError = 'Failed to load weeks list';
          this.showToast('Failed to load weeks list.', 'error');

          return of([[], []] as [YywwListItem[], YywwListItem[]]);
        }),
        finalize(() => {
          this.weeksIsLoading = false;
          this.safeDetectChanges();
        }),
      )
      .subscribe(([thisYear, prevYear]) => {
        const byKey = new Map<string, YywwListItem>();

        for (const week of [...(prevYear ?? []), ...(thisYear ?? [])]) {
          if (week?.fullYearWeek) {
            byKey.set(week.fullYearWeek, week);
          }
        }

        this.weeks = Array.from(byKey.values()).sort((a, b) =>
          String(a.startDate).localeCompare(String(b.startDate)),
        );

        this.setDefaultExportRangeFromWeeks();
        this.safeDetectChanges();
      });
  }

  private setDefaultExportRangeFromWeeks(): void {
    if (this.userTouchedExportRange || !this.weeks.length) {
      return;
    }

    const currentFullYearWeek = this.toFullYearWeek6(this.currentWeek.yyyyww);
    const currentStartIso = this.currentWeek.startDate.format('YYYY-MM-DD');

    const currentWeekItem =
      this.weeks.find((week) => week.fullYearWeek === currentFullYearWeek) ??
      this.weeks.find(
        (week) =>
          !!week?.startDate &&
          !!week?.endDate &&
          week.startDate <= currentStartIso &&
          currentStartIso <= week.endDate,
      );

    if (!currentWeekItem) {
      this.exportStartWeek = null;
      this.exportEndWeek = null;

      return;
    }

    const previousWeekItem =
      this.weeks
        .filter((week) => week.startDate < currentWeekItem.startDate)
        .reduce<YywwListItem | null>((best, week) => {
          if (!best) return week;

          return week.startDate > best.startDate ? week : best;
        }, null) ?? null;

    this.exportEndWeek = currentWeekItem.fullYearWeek;
    this.exportStartWeek = previousWeekItem?.fullYearWeek ?? currentWeekItem.fullYearWeek;
  }

  private toFullYearWeek6(value: string | number): string {
    const normalized = String(value ?? '').trim();

    if (!normalized) return '';

    if (normalized.length === 5) {
      return normalized.slice(0, 4) + normalized.slice(4).padStart(2, '0');
    }

    if (normalized.length === 6) {
      return normalized;
    }

    return normalized.padStart(6, '0');
  }

  private isValidFullYearWeek(value: string | number | null | undefined): boolean {
    const normalized = this.toFullYearWeek6(value ?? '');

    if (!/^\d{6}$/.test(normalized)) {
      return false;
    }

    const year = Number(normalized.slice(0, 4));
    const week = Number(normalized.slice(4, 6));

    return Number.isFinite(year) && Number.isFinite(week) && week >= 1 && week <= 53;
  }

  private buildWeekInfoFromFullYearWeek(value: string | number): WeekInfo {
    const normalized = this.toFullYearWeek6(value);
    const year = Number(normalized.slice(0, 4));
    const week = Number(normalized.slice(4, 6));

    const monday = dayjs(`${year}-01-04`).startOf('isoWeek').add(week - 1, 'week');

    return {
      yyyyww: normalized,
      startDate: monday,
      endDate: monday.add(6, 'day'),
    } as WeekInfo;
  }

  private syncWeekToUrl(replaceUrl = false): void {
    const week = this.toFullYearWeek6(this.currentWeek.yyyyww);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { week },
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  exportRangeToExcel(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const weeksInRange = this.getWeeksInSelectedRange();

    if (!weeksInRange.length) {
      this.showToast('Select a valid export range first.', 'error');

      return;
    }

    const requests = weeksInRange.map((week) =>
      this.timesheetService.getWeeklyTimesheetsByFilters(this.buildWeeklyFilters(week.fullYearWeek)).pipe(
        map((response: WeeklyTimesheetResponse) => response?.data),
        map((data) =>
          this.buildTimesheetMetaRowsFromWeeklyData(data, week.fullYearWeek, week.startDate),
        ),
      ),
    );

    this.isLoading = true;
    this.errorMessage = null;
    this.safeDetectChanges();

    forkJoin(requests)
      .pipe(
        take(1),
        catchError(() => {
          this.errorMessage = 'Export failed';
          this.showToast('Export failed.', 'error');

          return of([] as Array<{ fullYearWeek: string; rows: WeeklyTimesheetWithMeta[] }>);
        }),
        finalize(() => {
          this.isLoading = false;
          this.safeDetectChanges();
        }),
      )
      .subscribe((results) => {
        if (!results.length) return;

        const startDateByWeek = new Map<string, string>();

        for (const week of weeksInRange) {
          if (week?.fullYearWeek && week?.startDate) {
            startDateByWeek.set(week.fullYearWeek, week.startDate);
          }
        }

        const workbook = XLSX.utils.book_new();

        for (const result of results) {
          const startDateIso = startDateByWeek.get(result.fullYearWeek) ?? weeksInRange[0].startDate;
          const sheet = this.buildWorksheetForWeek(result.fullYearWeek, startDateIso, result.rows);

          XLSX.utils.book_append_sheet(workbook, sheet, this.exportSheetName(result.fullYearWeek));
        }

        const startLabel = this.exportStartWeek ?? 'START';
        const endLabel = this.exportEndWeek ?? 'END';

        const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        saveAs(
          new Blob([buffer], { type: 'application/octet-stream' }),
          `Weekly_Timesheet_${startLabel}_to_${endLabel}.xlsx`,
        );

        this.showToast('Excel export created successfully.', 'success');
      });
  }

  private getWeeksInSelectedRange(): YywwListItem[] {
    const start = this.exportStartWeek;
    const end = this.exportEndWeek;

    if (!start || !end) return [];

    const indexByWeek = new Map<string, number>();
    this.weeks.forEach((week, index) => indexByWeek.set(week.fullYearWeek, index));

    const startIndex = indexByWeek.get(start);
    const endIndex = indexByWeek.get(end);

    if (startIndex === undefined || endIndex === undefined) {
      return [];
    }

    const low = Math.min(startIndex, endIndex);
    const high = Math.max(startIndex, endIndex);

    return this.weeks.slice(low, high + 1);
  }

  private exportSheetName(fullYearWeek: string): string {
    return `Week ${fullYearWeek}`.slice(0, 31);
  }

  private buildDayHeadersForRange(startDateIso: string): string[] {
    const startDate = new Date(`${startDateIso}T00:00:00`);

    return DAY_LABELS.map((label, index) => {
      const date = new Date(startDate.getTime());
      date.setDate(startDate.getDate() + index);

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      return `${label} ${month}-${day}-${year}`;
    });
  }

  private buildWorksheetForWeek(
    fullYearWeek: string,
    startDateIso: string,
    timesheets: WeeklyTimesheetWithMeta[],
  ): XLSX.WorkSheet {
    const header = [
      'Last Name',
      'First Name',
      'Employee ID',
      ...this.buildDayHeadersForRange(startDateIso),
      'Total',
    ];

    const dailyTotals = Array(7).fill(0);

    for (const timesheet of timesheets ?? []) {
      for (let index = 0; index < 7; index += 1) {
        dailyTotals[index] += Number(timesheet.displayHours[index]?.number) || 0;
      }
    }

    const weeklyTotal = dailyTotals.reduce((sum, value) => sum + value, 0);
    const employeeCount = timesheets?.length || 1;
    const dailyAverages = dailyTotals.map((total) => total / employeeCount);
    const overallAverage = weeklyTotal / (employeeCount * 7);

    const rows = (timesheets ?? []).map((timesheet) => [
      timesheet.user.lastName,
      timesheet.user.firstName,
      timesheet.user.employeeNumber ?? timesheet.user.id,
      ...timesheet.displayHours.map((hours) => Number(Number(hours?.number ?? 0).toFixed(2))),
      Number(Number(timesheet.totalHours).toFixed(2)),
    ]);

    rows.push([]);
    rows.push([
      'Daily Total',
      '',
      '',
      ...dailyTotals.map((value) => Number(value.toFixed(2))),
      Number(weeklyTotal.toFixed(2)),
    ]);
    rows.push([
      'Daily Avg',
      '',
      '',
      ...dailyAverages.map((value) => Number(value.toFixed(2))),
      Number(overallAverage.toFixed(2)),
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
    worksheet['!cols'] = header.map(() => ({ wch: 16 }));

    return worksheet;
  }

  private buildTimesheetMetaRowsFromWeeklyData(
    data: WeeklyReportData,
    fullYearWeek: string,
    startDateIso: string,
  ): { fullYearWeek: string; rows: WeeklyTimesheetWithMeta[] } {
    return {
      fullYearWeek,
      rows: this.mapWeeklyRows(data, this.buildWeekKeysFromStartDate(startDateIso)),
    };
  }

  private mapWeeklyRows(
    data: WeeklyReportData | null | undefined,
    weekKeys: string[],
    previousRows: WeeklyTimesheetWithMeta[] = [],
  ): WeeklyTimesheetWithMeta[] {
    const clockedRows = data?.timeLogs ?? [];
    const bookedRows = data?.bookedhours ?? [];
    const bookedByUser = this.buildBookedByUserMap(bookedRows as WeeklyBookedHours[]);
    const scheduledByUser = this.buildScheduledByUserMap(data, weekKeys);

    const previousCheckedMap = new Map<number, boolean>(
      previousRows.map((row) => [row.user.id, !!row.autoFillToFortyChecked]),
    );

    const timesheetByUser = new Map<number, WeeklyTimesheet>();

    for (const timesheet of clockedRows) {
      if (!timesheet?.user?.id) continue;

      timesheetByUser.set(timesheet.user.id, {
        ...timesheet,
        logs: timesheet.logs ?? [],
      });
    }

    for (const bookedRow of bookedRows ?? []) {
      const user = bookedRow?.user;

      if (!user?.id || timesheetByUser.has(user.id)) {
        continue;
      }

      timesheetByUser.set(user.id, {
        user: user as User,
        logs: this.emptyDailyLogsForWeek(weekKeys),
        totalHours: 0,
      });
    }

    return Array.from(timesheetByUser.values())
      .sort((a, b) => {
        const lastNameCompare = String(a.user?.lastName ?? '').localeCompare(
          String(b.user?.lastName ?? ''),
        );

        if (lastNameCompare !== 0) return lastNameCompare;

        return String(a.user?.firstName ?? '').localeCompare(String(b.user?.firstName ?? ''));
      })
      .map((timesheet) => {
        const rowMeta = this.buildTimesheetRowMeta(timesheet, weekKeys, bookedByUser);

        const bookedWeeklyHours = this.round2(
          rowMeta.bookedHours.reduce((sum, value) => sum + (Number(value) || 0), 0),
        );

        const scheduledWeeklyHours = this.round2(
          scheduledByUser.get(timesheet.user.id)?.scheduledHours ?? 0,
        );

        const totalClass = this.getScheduledComparisonClass(bookedWeeklyHours, scheduledWeeklyHours);

        return {
          ...timesheet,
          ...rowMeta,
          totalHours: bookedWeeklyHours,
          totalClass,
          bookedWeeklyHours,
          scheduledWeeklyHours,
          bookedVsScheduledLabel: `${bookedWeeklyHours.toFixed(2)} / ${scheduledWeeklyHours.toFixed(2)}`,
          remainingHoursToForty: bookedWeeklyHours,
          autoFillToFortyChecked: !!previousCheckedMap.get(timesheet.user.id),
        } as WeeklyTimesheetWithMeta;
      });
  }

  private emptyDailyLogsForWeek(weekKeys: string[]): DailyLog[] {
    return weekKeys.map((date) => ({
      date,
      timeLogs: [],
    }));
  }

  private getScheduledComparisonClass(bookedHours: number, scheduledHours: number): TotalClass {
    const booked = this.round2(Number(bookedHours) || 0);
    const scheduled = this.round2(Number(scheduledHours) || 0);

    if (booked <= 0 && scheduled <= 0) {
      return 'total-scheduled-none';
    }

    return booked + 0.01 >= scheduled ? 'total-scheduled-met' : 'total-scheduled-under';
  }

  private buildScheduledByUserMap(
    data: WeeklyReportData | null | undefined,
    weekKeys: string[],
  ): Map<number, WeeklyScheduleMeta> {
    const scheduledByUser = new Map<number, WeeklyScheduleMeta>();
    const weekKeySet = new Set(weekKeys.map((date) => this.toDateKey(date)).filter(Boolean));

    const scheduleRows = ((data as unknown as { userSchedules?: unknown[] })?.userSchedules ?? []) as Array<{
      user?: {
        id?: number | string | null;
        schedules?: unknown[] | null;
      } | null;
      employee?: { id?: number | string | null } | null;
      userId?: number | string | null;
      employeeId?: number | string | null;
      schedules?: unknown[] | null;
      userAssignedSchedules?: unknown[] | null;
    }>;

    for (const row of scheduleRows) {
      const userId = Number(row?.user?.id ?? row?.employee?.id ?? row?.userId ?? row?.employeeId);

      if (!Number.isFinite(userId) || userId <= 0) {
        continue;
      }

      const schedules = [
        ...(row?.user?.schedules ?? []),
        ...(row?.schedules ?? []),
        ...(row?.userAssignedSchedules ?? []),
      ];

      if (!schedules.length) {
        continue;
      }

      const existing = scheduledByUser.get(userId) ?? {
        scheduledHours: 0,
        dailyScheduledHours: Array(7).fill(0),
      };

      for (const assigned of schedules) {
        const assignedDate = this.toDateKey(
          (assigned as { assignedDate?: string | null; date?: string | null })?.assignedDate ??
            (assigned as { assignedDate?: string | null; date?: string | null })?.date,
        );

        if (!assignedDate || (weekKeySet.size > 0 && !weekKeySet.has(assignedDate))) {
          continue;
        }

        const dayIndex = weekKeys.findIndex((date) => this.toDateKey(date) === assignedDate);
        const scheduledHours = this.computeAssignedScheduleHours(assigned);

        if (scheduledHours <= 0) {
          continue;
        }

        existing.scheduledHours = this.round2(existing.scheduledHours + scheduledHours);

        if (dayIndex >= 0) {
          existing.dailyScheduledHours[dayIndex] = this.round2(
            (existing.dailyScheduledHours[dayIndex] ?? 0) + scheduledHours,
          );
        }
      }

      scheduledByUser.set(userId, existing);
    }

    return scheduledByUser;
  }

  private computeAssignedScheduleHours(assigned: unknown): number {
    const item = assigned as {
      schedule?: {
        localStartTime?: string | null;
        localEndTime?: string | null;
        isOverNight?: boolean | null;
        breakTime?: string | null;
        breakThresholdHours?: number | null;
      } | null;
    };

    const schedule = item?.schedule;

    if (!schedule?.localStartTime || !schedule?.localEndTime) {
      return 0;
    }

    const startMinutes = this.parseTimeToMinutes(schedule.localStartTime);
    const endMinutes = this.parseTimeToMinutes(schedule.localEndTime);

    if (startMinutes === null || endMinutes === null) {
      return 0;
    }

    let durationMinutes = endMinutes - startMinutes;

    if (schedule.isOverNight || durationMinutes < 0) {
      durationMinutes += 24 * 60;
    }

    const thresholdHours = Number(schedule.breakThresholdHours ?? 0);
    const breakMinutes =
      thresholdHours > 0 && durationMinutes < thresholdHours * 60
        ? 0
        : this.parseBreakTimeToMinutes(schedule.breakTime);

    return this.round2(Math.max(0, durationMinutes - breakMinutes) / 60);
  }

  private parseTimeToMinutes(value: string | null | undefined): number | null {
    if (!value) {
      return null;
    }

    const [hoursRaw, minutesRaw] = String(value).split(':');
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);

    if (
      !Number.isFinite(hours) ||
      !Number.isFinite(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    return hours * 60 + minutes;
  }

  private parseBreakTimeToMinutes(value: string | null | undefined): number {
    if (!value) {
      return 0;
    }

    const [hoursRaw, minutesRaw] = String(value).split(':');
    let hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return 0;
    }

    if (hours === 12) {
      hours = 0;
    }

    return Math.max(0, hours * 60 + minutes);
  }

  private buildBookedByUserMap(
    bookedRows: WeeklyBookedHours[],
  ): Map<number, Map<string, BookedDayMeta>> {
    const bookedByUser = new Map<number, Map<string, BookedDayMeta>>();

    for (const userRow of bookedRows ?? []) {
      const dayMap = new Map<string, BookedDayMeta>();

      for (const day of userRow.bookedHours ?? []) {
        const dateKey = this.toDateKey(day.date);

        if (!dateKey) {
          continue;
        }

        const dayEntries = this.getBookedEntriesForDay(day).filter(
          (bookedHour) => (Number(bookedHour.hours) || 0) > 0,
        );

        if (!dayEntries.length) {
          continue;
        }

        const hours = dayEntries.reduce(
          (sum, bookedHour) => sum + (Number(bookedHour.hours) || 0),
          0,
        );

        const tooltip = dayEntries
          .map(
            (bookedHour) =>
              `${bookedHour.payCode?.name ?? 'N/A'} • ${bookedHour.workOrderNumber?.won ?? 'N/A'} • ${Number(
                bookedHour.hours,
              ).toFixed(2)}h`,
          )
          .join('\n');

        const normalizedPayCodes = dayEntries
          .map((bookedHour) =>
            String(bookedHour.payCode?.type ?? bookedHour.payCode?.name ?? '').trim().toLowerCase(),
          )
          .filter(Boolean);

        const hasHoliday = normalizedPayCodes.some((value) => value.includes('holiday'));

        const hasRequestedOfftime = normalizedPayCodes.some(
          (value) =>
            value.includes('vac') ||
            value.includes('pto') ||
            value.includes('sick') ||
            value.includes('personal') ||
            value.includes('bereav') ||
            value.includes('jury') ||
            value.includes('unpaid') ||
            value.includes('off'),
        );

        const isFullySupervisorApproved = dayEntries.every(
          (bookedHour) => !!bookedHour.supervisorApprovedDate,
        );

        const existing = dayMap.get(dateKey);

        dayMap.set(dateKey, {
          hours: this.round2((existing?.hours ?? 0) + hours),
          tooltip: [existing?.tooltip, tooltip].filter(Boolean).join('\n'),
          hasHoliday: !!existing?.hasHoliday || hasHoliday,
          hasRequestedOfftime: !!existing?.hasRequestedOfftime || hasRequestedOfftime,
          isFullySupervisorApproved:
            (existing ? existing.isFullySupervisorApproved : true) && isFullySupervisorApproved,
        });
      }

      if (userRow?.user?.id) {
        bookedByUser.set(userRow.user.id, dayMap);
      }
    }

    return bookedByUser;
  }

  private getBookedEntriesForDay(day: DailyBookedHours) {
    return day.bookedHours ?? day.bookedHour ?? [];
  }

  private buildTimesheetRowMeta(
    timesheet: WeeklyTimesheet,
    weekKeys: string[],
    bookedByUser: Map<number, Map<string, BookedDayMeta>>,
  ): Pick<
    WeeklyTimesheetWithMeta,
    | 'formattedTimeLogs'
    | 'bookedTooltips'
    | 'clockedHours'
    | 'bookedHours'
    | 'displayHours'
    | 'dailyClasses'
  > {
    const formattedTimeLogs = Array(7).fill('');
    const bookedTooltips = Array(7).fill('');
    const clockedHours = Array(7).fill(0);
    const bookedHours = Array(7).fill(0);
    const displayHours = Array.from({ length: 7 }, () => ({
      number: 0,
      backgroundColor: 'transparent',
    }));
    const dailyClasses = Array(7).fill('empty');

    const bookedMap = bookedByUser.get(timesheet.user.id);
    const logsByDate = this.buildLogsByDate(timesheet.logs);

    for (let index = 0; index < 7; index += 1) {
      const dateKey = this.toDateKey(weekKeys[index]);
      const dayLog = logsByDate.get(dateKey);
      const punches = dayLog?.timeLogs ?? [];

      let payCodeClass: string | null = null;
      let isFullySupervisorApproved = false;

      const hasClocked = punches.length > 0;
      let incomplete = false;

      if (hasClocked) {
        clockedHours[index] = this.round2(
          punches.reduce((sum, log) => sum + (Number(log.totalHours) || 0), 0),
        );

        formattedTimeLogs[index] = punches
          .map((log) => {
            if (!log.clockIn?.logTime || !log.clockOut?.logTime) {
              incomplete = true;
            }

            let outTime = this.formatLocalTime(log.clockOut?.logTime);

            if (log.clockIn?.logTime && log.clockOut?.logTime) {
              const inDate = new Date(`${log.clockIn.logTime}Z`);
              const outDate = new Date(`${log.clockOut.logTime}Z`);

              if (inDate.getUTCDate() !== outDate.getUTCDate()) {
                outTime += ' +1d';
              }
            }

            const missingPunch = !log.clockOut?.logTime ? ' (missing punch)' : '';

            return `In: ${this.formatLocalTime(log.clockIn?.logTime)}\nOut: ${outTime}${missingPunch}`;
          })
          .join('\n');
      }

      if (bookedMap?.has(dateKey)) {
        const bookedDay = bookedMap.get(dateKey)!;

        bookedHours[index] = Number(bookedDay.hours) || 0;
        bookedTooltips[index] = bookedDay.tooltip || '';
        isFullySupervisorApproved = bookedHours[index] > 0 && bookedDay.isFullySupervisorApproved;

        if (bookedHours[index] > 0) {
          if (bookedDay.hasHoliday) {
            payCodeClass = 'paycode-holiday';
          } else if (bookedDay.hasRequestedOfftime) {
            payCodeClass = 'requested-offtime';
          }
        }
      }

      const clocked = this.round2(Number(clockedHours[index]) || 0);
      const booked = this.round2(Number(bookedHours[index]) || 0);
      const diff = this.round2(clocked - booked);

      displayHours[index] = {
        number: booked,
        backgroundColor: 'transparent',
      };

      if (booked <= 0 && clocked <= 0) {
        dailyClasses[index] = 'empty';
        continue;
      }

      if (isFullySupervisorApproved) {
        dailyClasses[index] = 'approved';
        continue;
      }

      if (hasClocked && incomplete) {
        dailyClasses[index] = 'incomplete';
        continue;
      }

      if (payCodeClass === 'paycode-holiday') {
        dailyClasses[index] = 'paycode-holiday';
        continue;
      }

      if (payCodeClass === 'requested-offtime') {
        dailyClasses[index] = 'requested-offtime';
        continue;
      }

      if (Math.abs(diff) <= 0.01) {
        dailyClasses[index] = 'match';
      } else if (diff > 0) {
        dailyClasses[index] = 'over-booked';
      } else {
        dailyClasses[index] = 'under-booked';
      }
    }

    return {
      formattedTimeLogs,
      bookedTooltips,
      clockedHours,
      bookedHours,
      displayHours,
      dailyClasses,
    };
  }

  private buildWeekKeysFromStartDate(startDateIso: string): string[] {
    const startDate = new Date(`${startDateIso}T00:00:00`);
    const keys: string[] = [];

    for (let index = 0; index < 7; index += 1) {
      const date = new Date(startDate.getTime());
      date.setDate(startDate.getDate() + index);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      keys.push(`${year}-${month}-${day}`);
    }

    return keys;
  }

  get selectedCompanyId(): number | null {
    return this.filters.companyIds?.length ? this.filters.companyIds[0] : null;
  }

  set selectedCompanyId(value: number | null) {
    this.filters.companyIds = value !== null ? [value] : [];
  }

  get selectedDepartmentId(): number | null {
    return this.filters.departmentIds?.length ? this.filters.departmentIds[0] : null;
  }

  set selectedDepartmentId(value: number | null) {
    this.filters.departmentIds = value !== null ? [value] : [];
  }
}